import { z } from "zod";
import { apiBuilder, prefixApi } from "@zodios/core";

const sponsoredAdsAudienceFilterSchema = z.object({
  id: z.string(),
  value: z.object({
    op: z.enum([
      "eq",
      "ne",
      "gt",
      "gte",
      "lt",
      "lte",
      "in",
      "not_in",
      "ilike",
      "contains",
      "startswith",
      "endswith",
      "isnull",
      "between",
    ]),
    value: z.union([z.string(), z.number(), z.boolean(), z.array(z.union([z.string(), z.number()]))]),
  }),
});

export type SponsoredAdsAudienceFilter = z.infer<typeof sponsoredAdsAudienceFilterSchema>;

const sponsoredAdsAudienceMetricFields = {
  impressions: z.number(),
  impressions_prev: z.number().nullable().optional(),
  impressions_percent: z.number().nullable().optional(),
  impressions_delta: z.number().nullable().optional(),

  clicks: z.number(),
  clicks_prev: z.number().nullable().optional(),
  clicks_percent: z.number().nullable().optional(),
  clicks_delta: z.number().nullable().optional(),

  cost: z.number(),
  cost_prev: z.number().nullable().optional(),
  cost_percent: z.number().nullable().optional(),
  cost_delta: z.number().nullable().optional(),

  // The SB version of the audience report doesn't have conversions
  conversion: z.number().optional(),
  conversion_prev: z.number().nullable().optional(),
  conversion_percent: z.number().nullable().optional(),
  conversion_delta: z.number().nullable().optional(),

  ad_sales: z.number(),
  ad_sales_prev: z.number().nullable().optional(),
  ad_sales_percent: z.number().nullable().optional(),
  ad_sales_delta: z.number().nullable().optional(),

  ad_spend: z.number(),
  ad_spend_prev: z.number().nullable().optional(),
  ad_spend_percent: z.number().nullable().optional(),
  ad_spend_delta: z.number().nullable().optional(),

  click_thru_rate: z.number().nullable(),
  click_thru_rate_prev: z.number().nullable().optional(),
  click_thru_rate_percent: z.number().nullable().optional(),
  click_thru_rate_delta: z.number().nullable().optional(),

  conv_rate: z.number().nullable(),
  conv_rate_prev: z.number().nullable().optional(),
  conv_rate_percent: z.number().nullable().optional(),
  conv_rate_delta: z.number().nullable().optional(),

  cost_per_click: z.number().nullable(),
  cost_per_click_prev: z.number().nullable().optional(),
  cost_per_click_percent: z.number().nullable().optional(),
  cost_per_click_delta: z.number().nullable().optional(),

  roas: z.number().nullable(),
  roas_prev: z.number().nullable().optional(),
  roas_percent: z.number().nullable().optional(),
  roas_delta: z.number().nullable().optional(),

  acos: z.number().nullable(),
  acos_prev: z.number().nullable().optional(),
  acos_percent: z.number().nullable().optional(),
  acos_delta: z.number().nullable().optional(),
};

const sponsoredAdsAudienceRowSchema = z.object({
  org_id: z.union([z.string(), z.number()]).optional(),
  campaign_id: z.union([z.string(), z.number()]).optional(),
  segment_name: z.string().optional(),
  campaign_name: z.string().optional(),
  market_place: z.string().optional(),
  day: z.string().optional(),
  week: z.string().optional(),
  month: z.string().optional(),
  start_date: z.string().optional(),
  campaign_status: z.string().optional(),
  campaign_bidding_strategy: z.string().optional(),
  campaign_budget_type: z.string().optional(),
  ...sponsoredAdsAudienceMetricFields,
});

export type SponsoredAdsAudienceRow = z.infer<typeof sponsoredAdsAudienceRowSchema>;

const sponsoredAdsAudiencePaginationSchema = z.object({
  page: z.number(),
  limit: z.number().nullable(),
  total: z.number().nullable(),
});

const sponsoredAdsAudienceQueryRequestSchema = z.object({
  model: z.literal("spaudience").or(z.literal("sbaudience")),
  dimensions: z.union([z.string(), z.array(z.string())]),
  fields: z.union([z.string(), z.array(z.string())]).optional(),
  entity_mode: z.enum(["none", "join", "all", "without_stats"]).optional(),
  filters: z.array(sponsoredAdsAudienceFilterSchema).optional().default([]),
  sort: z.string().optional(),
  page: z.number().optional(),
  limit: z.number().optional(),
  include_count: z.boolean().optional(),
});

const sponsoredAdsAudienceQueryResponseSchema = z.object({
  data: z.array(sponsoredAdsAudienceRowSchema),
  pagination: sponsoredAdsAudiencePaginationSchema,
});

export const sponsoredAdsAudienceDatasetsApiDefinitions = prefixApi(
  "/api",
  apiBuilder({
    method: "post",
    path: "/datasets/query/?sponsoredadsaudience",
    alias: "sponsoredAdsAudienceDatasetsQuery",
    description: "Query SP audience datasets by dimensions + filters",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: sponsoredAdsAudienceQueryRequestSchema,
      },
    ],
    response: sponsoredAdsAudienceQueryResponseSchema,
  }).build(),
);
